/** @jsx jsx */
/** @jsxFrag Fragment */
import { jsx, Fragment } from 'hono/jsx/dom';
import { render } from 'hono/jsx/dom';
import { tr } from '../lib/i18n';

class ApplicationManager {
  private form: HTMLFormElement;
  private submitBtn: HTMLButtonElement;
  private waiverRequired: boolean;
  private waiverSigned: boolean = false;
  private startWaiverBtn: HTMLButtonElement | null;
  private embedContainer: HTMLDivElement | null;
  private waiverStatus: HTMLDivElement | null;
  private modal: HTMLDivElement | null;
  private modalCloseBtn: HTMLButtonElement | null;

  constructor() {
    this.form = document.getElementById('applicationForm') as HTMLFormElement;
    this.submitBtn = document.getElementById('submitBtn') as HTMLButtonElement;
    this.startWaiverBtn = document.getElementById('startWaiverBtn') as HTMLButtonElement | null;
    this.embedContainer = document.getElementById('docuseal-embed') as HTMLDivElement | null;
    this.waiverStatus = document.getElementById('waiver-status') as HTMLDivElement | null;
    this.modal = document.getElementById('waiver-modal') as HTMLDivElement | null;
    this.modalCloseBtn = document.getElementById('waiverModalClose') as HTMLButtonElement | null;
    this.waiverRequired = !!this.startWaiverBtn;

    if (this.form) {
      this.setupEventListeners();
    }
    if (this.waiverRequired && this.startWaiverBtn) {
      this.startWaiverBtn.addEventListener('click', this.handleStartWaiver.bind(this));
    }
    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener('click', this.closeModal.bind(this));
    }
    if (this.modal) {
      // Click outside content closes the modal
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal?.style.display !== 'none') this.closeModal();
      });
    }
  }

  private openModal(): void {
    if (!this.modal) return;
    this.modal.style.display = 'flex';
    document.body.classList.add('waiver-modal-open');
  }

  private closeModal(): void {
    if (!this.modal) return;
    this.modal.style.display = 'none';
    document.body.classList.remove('waiver-modal-open');
  }

  private setupEventListeners(): void {
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  /**
   * Build a 24-bit schedule string from checkboxes
   * Maps time slots to hours and creates a bitstring where 1 = available, 0 = not available
   */
  private buildScheduleBitString(dayType: 'weekday' | 'weekend'): string {
    const bitString = new Array(24).fill('0');

    // Map time slots to hour ranges
    const timeSlotMap: { [key: string]: number[] } = {
      '5_7': [5, 6],
      '8_10': [8, 9],
      '10_17': [10, 11, 12, 13, 14, 15, 16],
      '17_21': [17, 18, 19, 20],
      '22_24': [22, 23],
    };

    // Check each time slot checkbox
    Object.keys(timeSlotMap).forEach(slot => {
      const checkbox = this.form.querySelector(
        `input[name="schedule_${dayType}_${slot}"]`
      ) as HTMLInputElement;

      if (checkbox && checkbox.checked) {
        // Set the corresponding hours to '1'
        timeSlotMap[slot].forEach(hour => {
          bitString[hour] = '1';
        });
      }
    });

    return bitString.join('');
  }

  private setWaiverStatus(html: string, color: 'red' | 'green' | 'gray' = 'gray'): void {
    if (!this.waiverStatus) return;
    this.waiverStatus.innerHTML = `<p style="color:${color}">${html}</p>`;
  }

  private async handleStartWaiver(): Promise<void> {
    const name = (this.form.querySelector('input[name="name"]') as HTMLInputElement | null)?.value?.trim() || '';
    const email = (this.form.querySelector('input[name="email"]') as HTMLInputElement | null)?.value?.trim() || '';
    if (!name || !email) {
      this.setWaiverStatus(tr('Please enter your name and email above first.', 'Primero escribe tu nombre y correo arriba.'), 'red');
      return;
    }
    if (!this.startWaiverBtn || !this.embedContainer) return;

    this.startWaiverBtn.disabled = true;
    this.startWaiverBtn.textContent = tr('Loading…', 'Cargando…');
    this.setWaiverStatus('');

    try {
      const res = await fetch('/docuseal/start-waiver', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email }),
      });
      const body = await res.json();
      if (!res.ok || !body.success) {
        this.setWaiverStatus(body.error || tr('Failed to load waiver', 'No se pudo cargar la carta responsiva'), 'red');
        this.startWaiverBtn.disabled = false;
        this.startWaiverBtn.textContent = tr('Load waiver', 'Cargar carta responsiva');
        return;
      }
      if (body.alreadyCompleted) {
        this.markWaiverSigned();
        return;
      }

      // Mount the DocuSeal embed inside the modal (only once)
      if (this.embedContainer.children.length === 0) {
        const formEl = document.createElement('docuseal-form');
        formEl.setAttribute('data-src', body.embedSrc);
        formEl.setAttribute('data-email', email);
        formEl.setAttribute('data-name', name);
        formEl.addEventListener('completed', () => {
          this.handleWaiverCompleted();
        });
        formEl.addEventListener('declined', () => {
          this.setWaiverStatus(tr(
            'Waiver was declined. You must sign the waiver to submit an application.',
            'La carta responsiva fue rechazada. Debes firmarla para enviar tu solicitud.'
          ), 'red');
          this.closeModal();
        });
        this.embedContainer.appendChild(formEl);
      }
      this.openModal();
      this.startWaiverBtn.textContent = tr('Reopen waiver', 'Reabrir carta responsiva');
      this.startWaiverBtn.disabled = false;
      this.setWaiverStatus(tr('Please sign the waiver in the dialog.', 'Firma la carta responsiva en la ventana.'), 'gray');
    } catch (err) {
      console.error('Error starting waiver:', err);
      this.setWaiverStatus(tr('Network error loading waiver. Please try again.', 'Error de red al cargar la carta responsiva. Inténtalo de nuevo.'), 'red');
      this.startWaiverBtn.disabled = false;
      this.startWaiverBtn.textContent = tr('Load waiver', 'Cargar carta responsiva');
    }
  }

  private async handleWaiverCompleted(): Promise<void> {
    // Tell the server to fetch the latest status from DocuSeal in case the
    // webhook hasn't landed yet. We don't strictly need to wait for this,
    // but it prevents a race when the user immediately clicks submit.
    try {
      await fetch('/docuseal/refresh-status', { method: 'POST' });
    } catch (err) {
      console.warn('Failed to refresh waiver status:', err);
    }
    this.markWaiverSigned();
    // Brief pause so the user sees the completion animation in the embed, then close.
    setTimeout(() => this.closeModal(), 1500);
  }

  private markWaiverSigned(): void {
    this.waiverSigned = true;
    this.submitBtn.disabled = false;
    if (this.startWaiverBtn) {
      this.startWaiverBtn.style.display = 'none';
    }
    this.setWaiverStatus(tr('Waiver signed. You can now submit your application.', 'Carta responsiva firmada. Ya puedes enviar tu solicitud.'), 'green');
  }

  private async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();

    if (this.waiverRequired && !this.waiverSigned) {
      this.showError(tr('Please sign the waiver before submitting your application.', 'Firma la carta responsiva antes de enviar tu solicitud.'));
      return;
    }

    this.submitBtn.disabled = true;
    this.submitBtn.textContent = tr('Submitting...', 'Enviando...');

    try {
      const formData = new FormData(this.form);

      // Build schedule bitstrings from checkboxes
      const scheduleWeekdays = this.buildScheduleBitString('weekday');
      const scheduleWeekends = this.buildScheduleBitString('weekend');

      const applicationData = {
        name: formData.get('name'),
        email: formData.get('email'),
        intendedUsage: formData.get('intendedUsage'),
        aboutYou: formData.get('aboutYou'),
        slidingScaleDues: formData.get('slidingScaleDues'),
        howHeard: formData.get('howHeard'),
        scheduleWeekdays,
        scheduleWeekends,
      };

      console.log('Submitting application data:', applicationData);

      const response = await fetch('/submit-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Redirect to success page
          window.location.href = '/apply?success=true';
        } else {
          this.showError(result.error || tr('Failed to submit application', 'No se pudo enviar la solicitud'));
        }
      } else {
        const errorResult = await response.json();
        this.showError(errorResult.error || tr('Failed to submit application', 'No se pudo enviar la solicitud'));
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      this.showError(tr('Network error. Please try again.', 'Error de red. Inténtalo de nuevo.'));
    } finally {
      this.submitBtn.disabled = this.waiverRequired && !this.waiverSigned;
      this.submitBtn.textContent = tr('Submit Application', 'Enviar solicitud');
    }
  }

  private showError(message: string): void {
    // Remove existing error alerts
    const existingAlerts = document.querySelectorAll('.alert-danger');
    existingAlerts.forEach(alert => alert.remove());

    // Create new error alert
    const errorAlert = (
      <div className="alert alert-danger">
        {message}
      </div>
    );

    const container = document.querySelector('.container');
    const h2 = container?.querySelector('h2');
    if (h2 && h2.nextSibling) {
      const tempDiv = document.createElement('div');
      render(errorAlert, tempDiv);
      if (tempDiv.firstElementChild) {
        h2.parentNode?.insertBefore(tempDiv.firstElementChild, h2.nextSibling);
      }
    }

    // Scroll to top to show error
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ApplicationManager();
});
